
import React from 'react';
import { Language, UserStatistics } from '../../types';

interface ProfileOverviewProps {
  language: Language;
  totalRequests: number;
  timeSaved: number;
  activeServicesCount: number;
  statistics?: UserStatistics;
}

const SimpleLineChart = ({ data, color }: { data: number[], color: string }) => {
  if (!data || data.length === 0) return null;
  const max = Math.max(...data) || 1;
  const points = data.map((val, i) => `${i * (100 / (data.length - 1))},${100 - (val / max) * 100}`).join(' ');
  
  return (
    <svg viewBox="0 0 100 100" className="w-full h-full overflow-visible" preserveAspectRatio="none">
      <defs>
        <linearGradient id={`grad-${color}`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor={color} stopOpacity="0.2" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={`M0,100 L0,${100 - (data[0]/max)*100} ${points.split(' ').map(p => `L${p}`).join(' ')} L100,100 Z`} fill={`url(#grad-${color})`} stroke="none" />
      <polyline points={points} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
      {data.map((val, i) => (
        <circle key={i} cx={i * (100 / (data.length - 1))} cy={100 - (val / max) * 100} r="1.5" fill="white" stroke={color} strokeWidth="1" vectorEffect="non-scaling-stroke" />
      ))}
    </svg>
  );
};

const ProfileOverview: React.FC<ProfileOverviewProps> = ({ language, totalRequests, timeSaved, activeServicesCount, statistics }) => {
  const isRtl = language === 'ar';

  // Use real data from statistics object if available, otherwise fallbacks are handled in parent
  const activityData = statistics?.activityHistory || [0,0,0,0,0,0,0];
  const usageDistribution = statistics?.usageByService || [];
  const efficiency = statistics?.efficiencyScore || 0;

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
      
      {/* Stat Cards Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Requests */}
        <div className="bg-white dark:bg-[#15151a] p-5 rounded-[24px] border border-slate-100 dark:border-white/5 shadow-sm relative overflow-hidden group">
           <div className="absolute top-0 right-0 p-4 opacity-5 group-hover:opacity-10 transition-opacity"><i className="fas fa-bolt text-6xl text-slate-500"></i></div>
           <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-blue-500/10 flex items-center justify-center text-blue-500"><i className="fas fa-server"></i></div>
              <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider">{isRtl ? 'إجمالي الطلبات' : 'Total Requests'}</span>
           </div>
           <h3 className="text-3xl font-black text-slate-800 dark:text-white mb-1">{totalRequests.toLocaleString()}</h3>
           <p className="text-[10px] font-bold text-emerald-500 flex items-center gap-1"><i className="fas fa-arrow-up"></i> Live {isRtl ? 'هذا الأسبوع' : 'Tracking'}</p>
        </div>

        {/* Time Saved */}
        <div className="bg-white dark:bg-[#15151a] p-5 rounded-[24px] border border-slate-100 dark:border-white/5 shadow-sm relative overflow-hidden group">
           <div className="absolute top-0 right-0 p-4 opacity-5 group-hover:opacity-10 transition-opacity"><i className="fas fa-clock text-6xl text-slate-500"></i></div>
           <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center text-emerald-500"><i className="fas fa-stopwatch"></i></div>
              <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider">{isRtl ? 'الوقت الموفر' : 'Time Saved'}</span>
           </div>
           <h3 className="text-3xl font-black text-slate-800 dark:text-white mb-1">{timeSaved} <span className="text-sm font-bold text-slate-400">h</span></h3>
           <p className="text-[10px] font-bold text-emerald-500 flex items-center gap-1"><i className="fas fa-calculator"></i> {isRtl ? 'محسوبة تلقائياً' : 'Auto calculated'}</p>
        </div>

        {/* Active Services */}
        <div className="bg-white dark:bg-[#15151a] p-5 rounded-[24px] border border-slate-100 dark:border-white/5 shadow-sm relative overflow-hidden group">
           <div className="absolute top-0 right-0 p-4 opacity-5 group-hover:opacity-10 transition-opacity"><i className="fas fa-cubes text-6xl text-slate-500"></i></div>
           <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-purple-500/10 flex items-center justify-center text-purple-500"><i className="fas fa-layer-group"></i></div>
              <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider">{isRtl ? 'الخدمات النشطة' : 'Active Services'}</span>
           </div>
           <h3 className="text-3xl font-black text-slate-800 dark:text-white mb-1">{activeServicesCount}</h3>
           <p className="text-[10px] font-bold text-slate-400">{isRtl ? 'استخدام فريد' : 'Unique usage'}</p>
        </div>

        {/* Efficiency Score */}
        <div className="bg-white dark:bg-[#15151a] p-5 rounded-[24px] border border-slate-100 dark:border-white/5 shadow-sm relative overflow-hidden group">
           <div className="absolute top-0 right-0 p-4 opacity-5 group-hover:opacity-10 transition-opacity"><i className="fas fa-chart-line text-6xl text-slate-500"></i></div>
           <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center text-amber-500"><i className="fas fa-gauge-high"></i></div>
              <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider">{isRtl ? 'معدل الكفاءة' : 'Efficiency Score'}</span>
           </div>
           <h3 className="text-3xl font-black text-slate-800 dark:text-white mb-1">{efficiency}%</h3>
           <div className="w-full bg-slate-100 dark:bg-white/10 h-1.5 rounded-full mt-2 overflow-hidden">
              <div className="h-full bg-amber-500 rounded-full" style={{ width: `${efficiency}%` }}></div>
           </div>
        </div>
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
         
         {/* Main Activity Chart */}
         <div className="lg:col-span-2 bg-white dark:bg-[#15151a] p-6 lg:p-8 rounded-[32px] border border-slate-100 dark:border-white/5 shadow-sm">
            <div className="flex justify-between items-center mb-8">
               <div>
                  <h3 className="text-lg font-black text-slate-800 dark:text-white">{isRtl ? 'نشاط الذكاء الاصطناعي' : 'AI Activity Overview'}</h3>
                  <p className="text-xs text-slate-400 font-medium">{isRtl ? 'تحليل الطلبات خلال آخر 7 أيام' : 'Request analysis for the last 7 days'}</p>
               </div>
               <div className="flex gap-2">
                  <span className="px-3 py-1 rounded-lg bg-emerald-500/10 text-emerald-500 text-[10px] font-black">Live</span>
               </div>
            </div>
            <div className="h-64 w-full">
               <SimpleLineChart data={activityData} color="#10b981" />
            </div>
            <div className="flex justify-between mt-4 text-[10px] font-bold text-slate-400 uppercase tracking-wider">
               <span>Mon</span><span>Tue</span><span>Wed</span><span>Thu</span><span>Fri</span><span>Sat</span><span>Sun</span>
            </div>
         </div>

         {/* Distribution Chart */}
         <div className="bg-white dark:bg-[#15151a] p-6 lg:p-8 rounded-[32px] border border-slate-100 dark:border-white/5 shadow-sm flex flex-col">
            <h3 className="text-lg font-black text-slate-800 dark:text-white mb-6">{isRtl ? 'توزيع الاستهلاك' : 'Usage Breakdown'}</h3>
            <div className="flex-1 flex flex-col justify-center space-y-6">
               {usageDistribution.length > 0 ? usageDistribution.map((item, i) => (
                  <div key={i}>
                     <div className="flex justify-between mb-2">
                        <span className="text-xs font-bold text-slate-600 dark:text-slate-300">{item.label}</span>
                        <span className="text-xs font-black text-slate-800 dark:text-white">{item.count}%</span>
                     </div>
                     <div className="w-full bg-slate-100 dark:bg-white/5 h-2.5 rounded-full overflow-hidden">
                        <div className={`h-full rounded-full ${item.color}`} style={{ width: `${item.count}%` }}></div>
                     </div>
                  </div>
               )) : (
                 <div className="text-center text-slate-400 text-xs font-bold py-10">No usage data yet.</div>
               )}
            </div>
            <div className="mt-6 pt-6 border-t border-slate-100 dark:border-white/5">
               <button className="w-full py-3 rounded-xl bg-slate-50 dark:bg-white/5 text-slate-500 dark:text-slate-400 text-xs font-black hover:bg-slate-100 dark:hover:bg-white/10 transition-colors">
                  {isRtl ? 'تحميل التقرير الكامل' : 'Download Full Report'}
               </button>
            </div>
         </div>
      </div>
    </div>
  );
};

export default ProfileOverview;
